import React, { useState, useEffect, useRef } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Deck from "@/components/Deck";
import CentralMixer from "@/components/CentralMixer";
import TrackLibrary from "@/components/TrackLibrary";
import PaywallModal from "@/components/PaywallModal";
import AuthModal from "@/components/AuthModal";
import Visualizer from "@/components/Visualizer";
import GoLiveButton from "@/components/GoLiveButton";
import DjTipToast from "@/components/DjTipToast";
import FollowFeed from "@/components/FollowFeed";
import FollowSuggestions from "@/components/FollowSuggestions";
import StudioTrending from "@/components/StudioTrending";
import { useAuth } from "@/context/AuthContext";
import { useMidiContext } from "@/context/MidiContext";
import { useLink } from "@/context/LinkContext";
import LinkStatusPill from "@/components/LinkStatusPill";
import BindLastMidiPill from "@/components/BindLastMidiPill";
import { subscribeToPush, pushSupported, pushPermission } from "@/utils/push";
import { Bell, BarChart3, Shield, Trophy, FolderOpen, Settings, Wallet } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Radio, Save, Crown, LogIn } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const initialDeck = () => ({
  track: null,
  playing: false,
  pitch: 0,
  volume: 0.75,
  eqLow: 0.5, eqMid: 0.5, eqHigh: 0.5,
  hotCues: [null, null, null, null],
  loop: { bars: 0, start: null, end: null }, // bars=0 means off
});

export default function Studio() {
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [activeDeck, setActiveDeck] = useState("A");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 900);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (pushSupported()) setPushEnabled(pushPermission() === "granted");
  }, [user]);

  const enableNotifications = async () => {
    const ok = await subscribeToPush();
    if (ok) { setPushEnabled(true); toast.success("Notifications enabled."); }
    else toast.error("Notification permission denied.");
  };

  useEffect(() => {
    if (searchParams.get("spotify_connected") === "1") {
      toast.success("Spotify connected. Loading your library…");
      searchParams.delete("spotify_connected");
      setSearchParams(searchParams, { replace: true });
    }
    const err = searchParams.get("spotify_error");
    if (err) {
      toast.error(`Spotify: ${err}`);
      searchParams.delete("spotify_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const [decks, setDecks] = useState({
    A: initialDeck(), B: initialDeck(), C: initialDeck(), D: initialDeck(),
  });
  const [crossfader, setCrossfader] = useState(0.5);
  const [masterVolume, setMasterVolume] = useState(0.8);

  const setDeckState = (id, updater) => {
    setDecks(prev => ({ ...prev, [id]: typeof updater === "function" ? updater(prev[id]) : updater }));
  };

  // ─────────── Pioneer DJ / MIDI hardware bridge ───────────
  // Every parsed MIDI event lands here via the shared MidiContext. We map
  // the logical action → deck state mutation. Continuous controls (CC) are
  // normalised to 0..1; jog wheel & pitch are delta-nudge style.
  const midi = useMidiContext();
  const link = useLink();
  const [followLink, setFollowLink] = useState(false);
  // First-time reward: when the bridge WS transitions from disconnected → connected
  // we drop a subtle toast so DJs get a payoff moment for installing the companion
  // app. `nd-link-toast-seen` guards against repeat firings across reloads — clear
  // it in localStorage to see it again for testing.
  const linkPrevConnectedRef = useRef(false);
  useEffect(() => {
    if (link.connected && !linkPrevConnectedRef.current) {
      linkPrevConnectedRef.current = true;
      const seen = (() => { try { return localStorage.getItem("nd-link-toast-seen") === "1"; } catch { return false; } })();
      if (!seen) {
        try { localStorage.setItem("nd-link-toast-seen", "1"); } catch { /* noop */ }
        toast.success("Ableton Link · Connected", {
          description: link.peersCount > 0
            ? `Locked to ${link.peersCount} peer${link.peersCount === 1 ? "" : "s"} · session ${link.sessionBpm?.toFixed(1) ?? "--"} BPM. Tap Follow BPM to sync Deck A.`
            : "Bridge is ready — open Serato / Traktor / Rekordbox / Ableton Live and enable Link.",
          duration: 6000,
        });
      }
    }
    if (!link.connected) linkPrevConnectedRef.current = false;
  }, [link.connected, link.peersCount, link.sessionBpm]);
  // Active deck for Link tempo-follow. When multiple decks are playing we
  // just target Deck A — DJs can remap later.
  const followTargetDeck = "A";
  useEffect(() => {
    if (!followLink || !link.connected || !link.sessionBpm) return;
    const targetBpm = link.sessionBpm;
    setDecks(prev => {
      const t = prev[followTargetDeck];
      if (!t?.bpm || !targetBpm) return prev;
      // Convert BPM delta to Pioneer-style pitch percent (default range ±8 %).
      const pct = Math.max(-8, Math.min(8, ((targetBpm - t.bpm) / t.bpm) * 100));
      return { ...prev, [followTargetDeck]: { ...t, pitch: pct } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followLink, link.connected, link.sessionBpm]);
  // Track jog-touch state per deck outside of React state — we don't want a
  // re-render on every touch/rotate, we just want the LATEST touched flag
  // available inside the MIDI event handler.
  const jogTouchRef = useRef({ A: false, B: false, C: false, D: false });

  // First-time hint when the auto-detected preset is a GENERIC one (clone or
  // catch-all). Real Pioneer users match a specific model and never see this;
  // clone owners get one gentle nudge that Learn is available. Keyed in
  // localStorage so the hint only fires once per browser per generic model.
  useEffect(() => {
    const model = midi.detectedModel;
    if (!model) return;
    // Fire-and-forget telemetry ping so we can see which presets vs.
    // catch-alls fire in the wild. localStorage-guarded so it's 1 row per
    // browser per model — never repeats.
    (() => {
      const firstInp = midi.inputs?.[0];
      const key = `nd-midi-model-reported-${model.id}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
      axios.post(`${API}/telemetry/midi-model`, {
        model_id: model.id,
        model_label: model.label,
        name: firstInp?.name || "",
        manufacturer: firstInp?.manufacturer || "",
      }).catch(() => { /* best-effort */ });
    })();
    const genericIds = new Set(["serato-clone-4deck", "pioneer-any-2deck"]);
    if (!genericIds.has(model.id)) return;
    const seenKey = `nd-generic-midi-hint-${model.id}`;
    if (localStorage.getItem(seenKey)) return;
    localStorage.setItem(seenKey, "1");
    toast.info(`Mapped as "${model.label}"`, {
      description: "Generic factory map — if a pad or knob feels off, twist it once and use the Bind pill in the corner to remap.",
      duration: 8000,
    });
  }, [midi.detectedModel]);

  useEffect(() => {
    const DECK_IDS = ["A", "B", "C", "D"];
    const unsub = midi.subscribe(({ deck, action, event }) => {
      const norm = event.kind === "cc" ? (event.value / 127) : 1;
      const deckId = deck >= 0 && deck < DECK_IDS.length ? DECK_IDS[deck] : null;
      switch (action) {
        case "play":
          if (event.kind === "noteon" && deckId) {
            setDeckState(deckId, s => ({ ...s, playing: !s.playing }));
          }
          break;
        case "cue":
          if (event.kind === "noteon" && deckId) {
            // Restart from 0 on cue-press; on release the audio engine
            // handles resume in Deck.jsx's own audioRef logic.
            setDeckState(deckId, s => ({ ...s, playing: false }));
          }
          break;
        case "sync":
          if (event.kind === "noteon" && deckId) onSync(deckId);
          break;
        case "volume":
          if (deckId) setDeckState(deckId, s => ({ ...s, volume: norm }));
          break;
        case "eq_low":
        case "eq_mid":
        case "eq_high":
          if (deckId) setDeckState(deckId, s => ({ ...s, [action === "eq_low" ? "eqLow" : action === "eq_mid" ? "eqMid" : "eqHigh"]: norm }));
          break;
        case "tempo":
          // Pitch fader: MIDI 0..127 → -8%..+8% (Pioneer default range)
          if (deckId) setDeckState(deckId, s => ({ ...s, pitch: (norm - 0.5) * 16 }));
          break;
        case "crossfader":
          setCrossfader(norm);
          break;
        case "hotcue_1":
        case "hotcue_2":
        case "hotcue_3":
        case "hotcue_4":
          if (event.kind === "noteon" && deckId) {
            const idx = parseInt(action.split("_")[1], 10) - 1;
            window.dispatchEvent(new CustomEvent(`nd-hotcue-${deckId}`, { detail: { idx } }));
          }
          break;
        case "jog_touch":
          // Top-platter capacitive touch. Note-on = start scratching, note-off
          // = release back to nudge mode. Deck.jsx uses this to pause playback
          // while touched so the audio doesn't fight the user's scrub input.
          if (deckId) {
            const touching = event.kind === "noteon";
            jogTouchRef.current[deckId] = touching;
            window.dispatchEvent(new CustomEvent(`nd-jog-touch-${deckId}`, { detail: { touching } }));
          }
          break;
        case "jog": {
          // Pioneer jog wheels send a 7-bit CC where 64 = idle (values below
          // 64 = counter-clockwise, above = clockwise). Technics MK7 sends
          // 14-bit pitch-bend where 8192 = idle. Both are normalised here
          // to a signed delta in the range roughly -64..+64.
          if (!deckId) break;
          let delta = 0;
          if (event.kind === "cc") {
            delta = event.value - 64;
          } else if (event.kind === "pb") {
            delta = (event.value - 8192) / 128;
          } else {
            break;
          }
          if (delta === 0) break;
          if (jogTouchRef.current[deckId]) {
            // Scrub mode — jump the playhead by (delta * 0.01) seconds
            window.dispatchEvent(new CustomEvent(`nd-jog-scrub-${deckId}`, { detail: { seconds: delta * 0.01 } }));
          } else {
            // Nudge mode — temporarily bend playbackRate by up to ±3 %
            window.dispatchEvent(new CustomEvent(`nd-jog-nudge-${deckId}`, { detail: { rateDelta: delta / 64 * 0.03 } }));
          }
          break;
        }
        default:
          // Loop / load — reserved for future deep integration
          break;
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midi]);

  const loadTrackToDeck = (deckId, track) => {
    if (track.source === "spotify" && !track.source_id) {
      toast.info("This Spotify track has no preview clip — Spotify doesn't allow raw audio streaming for mixing. Upload the MP3 or pick a track that has a preview.");
      return;
    }
    setDecks(prev => ({
      ...prev,
      [deckId]: {
        ...prev[deckId],
        track, playing: false, pitch: 0,
        hotCues: [null, null, null, null],
        loop: { bars: 0, start: null, end: null },
      },
    }));
    toast.success(`Loaded to Deck ${deckId}`);
  };

  // Sync: match tempo of pressed deck to the currently-playing deck
  const onSync = async (id) => {
    const target = decks[id];
    if (!target.track) return;
    const master = ["A", "B", "C", "D"].map(d => decks[d]).find(d => d.playing && d.track && d.track.id !== target.track.id);
    if (!master) { toast.info("SYNC needs another deck already playing"); return; }
    const masterBpm = (master.track.bpm || 120) * (1 + master.pitch / 100);
    const targetBpm = target.track.bpm || 120;
    const pitchAdjust = ((masterBpm - targetBpm) / targetBpm) * 100;
    setDeckState(id, s => ({ ...s, pitch: Math.max(-16, Math.min(16, pitchAdjust)) }));
    toast.success(`Deck ${id} synced to ${masterBpm.toFixed(1)} BPM`);
  };

  const onNeuralMix = async () => {
    const playing = ["A", "B", "C", "D"].map(id => ({ id, d: decks[id] })).filter(x => x.d.playing && x.d.track);
    if (playing.length < 2) { toast.info("Neural Mix needs 2 decks playing"); return; }
    const [from, to] = playing;
    try {
      const r = await axios.post(`${API}/ai/transition`, {
        deck_from: { title: from.d.track.title, bpm: (from.d.track.bpm || 120) * (1 + from.d.pitch / 100), key: from.d.track.key },
        deck_to: { title: to.d.track.title, bpm: (to.d.track.bpm || 120) * (1 + to.d.pitch / 100), key: to.d.track.key },
      });
      toast.success(r.data.coaching, { duration: 6000 });
      // Auto crossfade
      const start = crossfader;
      const target = ["A", "C"].includes(from.id) ? 1 : 0;
      const steps = 40;
      let i = 0;
      const iv = setInterval(() => {
        i++;
        const p = i / steps;
        setCrossfader(start + (target - start) * p);
        if (i >= steps) clearInterval(iv);
      }, 100);
    } catch { toast.error("Neural Mix failed"); }
  };

  const saveMix = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!user.is_premium) { setPaywallOpen(true); return; }
    const name = prompt("Name this mix session:");
    if (!name) return;
    try {
      await axios.post(`${API}/mixes`, { name, deck_states: decks, notes: "" });
      toast.success("Mix saved to your PRO cloud.");
    } catch (e) {
      if (e.response?.status === 402) setPaywallOpen(true);
      else toast.error("Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white relative">
      <Visualizer/>
      <DjTipToast/>
      {/* HEADER */}
      <header className="h-16 border-b border-[#1E2638] bg-[#0B0E14]/90 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="studio-home-button">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </button>

        <div className="hidden md:flex items-center gap-3 font-mono text-xs text-[#94A3B8] uppercase tracking-widest">
          <span className="px-2 py-1 rounded border border-[#1E2638]" data-testid="active-source-local">LOCAL</span>
          <span className="px-2 py-1 rounded border border-[#1E2638]">SPOTIFY</span>
          <span className="px-2 py-1 rounded border border-[#1E2638]">DEMO</span>
          <LinkStatusPill follow={followLink} onToggleFollow={() => setFollowLink(v => !v)}/>
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="save-mix-button" onClick={saveMix} variant="outline" className="border-[#2A354B] hover:border-[#00F0FF] bg-transparent text-white hover:text-[#00F0FF] font-black uppercase tracking-widest text-xs">
            <Save className="w-4 h-4 mr-1.5"/> Save Mix
          </Button>
          {user && <GoLiveButton decks={decks} token={token}/>}
          <Button data-testid="header-import-music-button"
            onClick={() => window.dispatchEvent(new Event("nd-browse-local"))}
            className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest text-xs">
            <FolderOpen className="w-4 h-4 mr-1.5"/> Import Music
          </Button>
          <Button data-testid="leaderboard-button" onClick={() => navigate("/leaderboard")} variant="outline"
            className="border-[#2A354B] hover:border-[#FFC107] hover:text-[#FFC107] bg-transparent text-white font-black uppercase tracking-widest text-xs hidden md:inline-flex">
            <Trophy className="w-4 h-4 mr-1.5"/> Leaderboard
          </Button>
          {user && (
            <Button data-testid="wallet-button" onClick={() => navigate("/wallet")} variant="outline"
              className="border-[#2A354B] hover:border-[#10B981] hover:text-[#10B981] bg-transparent text-white font-black uppercase tracking-widest text-xs hidden md:inline-flex">
              <Wallet className="w-4 h-4 mr-1.5"/> Wallet
            </Button>
          )}
          {user && pushSupported() && !pushEnabled && (
            <Button data-testid="enable-notifications-button" onClick={enableNotifications} variant="outline"
              className="border-[#2A354B] hover:border-[#FFC107] hover:text-[#FFC107] bg-transparent text-[#94A3B8] font-black uppercase tracking-widest text-xs">
              <Bell className="w-4 h-4 mr-1.5"/> Notify
            </Button>
          )}
          {user ? (
            <>
              {!user.is_premium && (
                <Button data-testid="upgrade-pro-modal-button" onClick={() => setPaywallOpen(true)} className="bg-[#FF2A6D] text-white hover:bg-[#FF5588] font-black uppercase tracking-widest text-xs">
                  <Crown className="w-4 h-4 mr-1.5"/> Upgrade
                </Button>
              )}
              {user.is_premium && user.trial && (
                <Button data-testid="keep-pro-button" onClick={() => setPaywallOpen(true)} className="bg-[#FF2A6D] text-white hover:bg-[#FF5588] font-black uppercase tracking-widest text-xs">
                  <Crown className="w-4 h-4 mr-1.5"/> Keep PRO · CA$4.99/mo
                </Button>
              )}
              {user.is_premium && <div className="px-3 py-1.5 rounded bg-[#00F0FF]/20 border border-[#00F0FF] font-mono text-[10px] uppercase tracking-widest text-[#00F0FF]" data-testid="pro-badge">
                {user.trial ? `TRIAL · ${user.trial_days_left}d LEFT` : "PRO UNLOCKED"}
              </div>}
              {user.is_premium && (
                <Button data-testid="analytics-button" onClick={() => navigate("/analytics")} variant="outline"
                  className="border-[#2A354B] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-white font-black uppercase tracking-widest text-xs">
                  <BarChart3 className="w-4 h-4 mr-1.5"/> Analytics
                </Button>
              )}
              {user.is_admin && (
                <Button data-testid="admin-button" onClick={() => navigate("/admin")} variant="outline"
                  className="border-[#EF4444] hover:bg-[#EF4444] hover:text-white bg-transparent text-[#EF4444] font-black uppercase tracking-widest text-xs">
                  <Shield className="w-4 h-4 mr-1.5"/> Admin
                </Button>
              )}
              <span className="hidden sm:inline text-xs font-mono text-[#94A3B8]" data-testid="studio-user-name">{user.display_name}</span>
              <Button data-testid="studio-settings-button" onClick={() => navigate("/settings")} variant="outline"
                className="border-[#1E2638] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-[#94A3B8] font-black uppercase tracking-widest text-xs" title="Settings · MIDI · Payouts · Tip jar · Genre">
                <Settings className="w-4 h-4 mr-1.5"/> Settings
              </Button>
              <Button data-testid="studio-logout-button" onClick={logout} variant="ghost" className="text-[#94A3B8] hover:text-white text-xs">Logout</Button>
            </>
          ) : (
            <Button data-testid="login-modal-open-button" onClick={() => setAuthOpen(true)} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest text-xs">
              <LogIn className="w-4 h-4 mr-1.5"/> Log in
            </Button>
          )}
        </div>
      </header>

      <FollowFeed/>
      <FollowSuggestions/>
      <StudioTrending/>

      {user?.trial && user.trial_days_left != null && user.trial_days_left <= 2 && (
        <div className="bg-gradient-to-r from-[#FF2A6D] to-[#FF8C00] px-6 py-2 text-center font-mono text-xs uppercase tracking-widest text-black relative z-30" data-testid="trial-countdown-banner">
          <span className="font-black">TRIAL ENDS IN {user.trial_days_left} DAY{user.trial_days_left === 1 ? "" : "S"}</span>
          <button onClick={() => setPaywallOpen(true)} className="ml-4 underline font-black">Keep PRO for CA$4.99/mo →</button>
        </div>
      )}

      {/* MIDI connected but no preset matched — surface it inline so the user
          doesn't sit twisting knobs while the events silently drop. */}
      {midi.inputs.length > 0 && !midi.detectedModel && (
        <div data-testid="midi-unmatched-banner" className="mx-auto max-w-[1920px] px-3 mt-3">
          <div className="rounded-lg border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-4 py-3 flex items-start gap-3">
            <span className="text-[#F59E0B] font-mono text-[10px] uppercase tracking-widest pt-0.5">// MIDI hint</span>
            <div className="flex-1 text-sm text-white">
              Detected <span className="font-mono text-[#F59E0B]">{midi.inputs[0]?.name || "your controller"}</span> but no
              factory preset matched. Physical knobs / buttons won't drive the decks until you either pick your
              model manually or run <b>Learn</b> in Settings.
            </div>
            <Link to="/settings#midi" data-testid="midi-unmatched-settings-link"
                  className="shrink-0 px-3 py-1 rounded bg-[#F59E0B] text-black text-xs font-black uppercase tracking-widest hover:bg-[#FBBF24] transition-colors">
              Open MIDI settings →
            </Link>
          </div>
        </div>
      )}

      {/* 4-DECK COCKPIT */}
      <main className="p-3 grid grid-cols-1 lg:grid-cols-12 gap-3 max-w-[1920px] mx-auto relative z-10">
        {isMobile ? (
          <>
            {/* Mobile: deck tab bar + single deck view */}
            <div className="col-span-12 flex items-center gap-2 sticky top-16 z-20 bg-[#0B0E14]/95 py-2" data-testid="mobile-deck-tabs">
              {["A", "B", "C", "D"].map(id => {
                const color = { A: "#00F0FF", B: "#FF8C00", C: "#00FF66", D: "#FF0055" }[id];
                return (
                  <button key={id} data-testid={`deck-tab-${id.toLowerCase()}`} onClick={() => setActiveDeck(id)}
                    className="flex-1 py-2 rounded font-display font-black text-lg transition-colors"
                    style={{
                      background: activeDeck === id ? color : "transparent",
                      color: activeDeck === id ? "#000" : color,
                      border: `2px solid ${color}`,
                    }}>{id}</button>
                );
              })}
            </div>
            <div className="col-span-12"
              onTouchStart={(e) => { window._touchX = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = (e.changedTouches[0].clientX) - (window._touchX || 0);
                const order = ["A", "B", "C", "D"];
                const idx = order.indexOf(activeDeck);
                if (Math.abs(dx) > 60) {
                  if (dx < 0 && idx < 3) setActiveDeck(order[idx + 1]);
                  if (dx > 0 && idx > 0) setActiveDeck(order[idx - 1]);
                }
              }}
              data-testid="mobile-deck-swipe">
              <Deck id={activeDeck} state={decks[activeDeck]} setState={u => setDeckState(activeDeck, u)} onSync={onSync}/>
            </div>
            <div className="col-span-12">
              <CentralMixer decks={decks} crossfader={crossfader} setCrossfader={setCrossfader} masterVolume={masterVolume} setMasterVolume={setMasterVolume} onNeuralMix={onNeuralMix}/>
            </div>
          </>
        ) : (
          <>
            {/* Desktop: 2x2 grid */}
            <div className="lg:col-span-5"><Deck id="A" state={decks.A} setState={u => setDeckState("A", u)} onSync={onSync}/></div>
            <div className="lg:col-span-2 lg:row-span-2"><CentralMixer decks={decks} crossfader={crossfader} setCrossfader={setCrossfader} masterVolume={masterVolume} setMasterVolume={setMasterVolume} onNeuralMix={onNeuralMix}/></div>
            <div className="lg:col-span-5"><Deck id="B" state={decks.B} setState={u => setDeckState("B", u)} onSync={onSync}/></div>
            <div className="lg:col-span-5"><Deck id="C" state={decks.C} setState={u => setDeckState("C", u)} onSync={onSync}/></div>
            <div className="lg:col-span-5"><Deck id="D" state={decks.D} setState={u => setDeckState("D", u)} onSync={onSync}/></div>
          </>
        )}

        {/* Library full width */}
        <div className="lg:col-span-12 min-h-[400px]">
          <TrackLibrary decks={decks} loadTrackToDeck={loadTrackToDeck}/>
        </div>
      </main>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => refreshUser()}/>
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen}/>
      <BindLastMidiPill midi={midi}/>
    </div>
  );
}
